import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { FormikHelpers, useFormik } from 'formik';

import { API, ResponseStatus } from 'app/api';
import { URL } from 'app/api/constants';
import { FormControl } from 'app/design-system/components/FormControl';
import { StepperContext } from 'app/design-system/components/Stepper/context';
import { reLogin } from 'app/redux/actions/auth';
import type { CodeFormValue } from 'app/widgets/CodeConfirmation';
import {
  CODE_FORM_VALUES,
  CODE_LENGTH,
  CodeConfirmation,
  CodeError,
} from 'app/widgets/CodeConfirmation';

import { CHECK_ICON_TIME_DURATION } from '../../../../constants';
import { RegContext } from '../../../../context';

const NOT_NUMBER = /\D/g;

export const SecondStepForm: FC = () => {
  const { activeStep, backwardStep, forwardStep } = useContext(StepperContext);
  const { regData, setRegData } = useContext(RegContext);
  const dispatch = useDispatch();
  const history = useHistory();

  const [error, setError] = useState<CodeError | null>(regData.codeError);

  const { sessionToken, phoneToken, requisite, disabled } = regData;

  useEffect(() => {
    setRegData((prevState) => ({ ...prevState, regStep: activeStep }));
  }, []);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setTouched,
    setFieldValue,
  } = useFormik<CodeFormValue>({
    initialValues: CODE_FORM_VALUES,
    onSubmit: async (values: CodeFormValue, formikHelpers: FormikHelpers<CodeFormValue>) => {
      const { setSubmitting } = formikHelpers;
      const { code } = values;

      if (code.length < CODE_LENGTH) {
        setError(CodeError.NOT_CODE);
        return setSubmitting(false);
      }

      if (sessionToken === null || phoneToken === null) {
        setError(CodeError.NOT_TOKEN);
        return setSubmitting(false);
      }

      setRegData((prevState) => ({ ...prevState, disabled: true }));

      API.registration.sendCheckCodeAuth({ phoneNumber: regData.requisite, code })
        .then((response) => {
          if (response.status === ResponseStatus.SUCCESS) {
            setError(null);
            setRegData((prevState) => ({ ...prevState, checked: true }));

            const isNotAlreadyExisted = response?.data?.isAlreadyExisted === false;

            setTimeout(() => {
              if (isNotAlreadyExisted){
                forwardStep();
                return setRegData((prevState) => ({ ...prevState, checked: false }));
              }

              dispatch(reLogin());
              history.replace({ search: '' });
            }, CHECK_ICON_TIME_DURATION);
          }
          else if (response.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
            response.data.type.endsWith('secure-code-token-not-found')
              ? setError(CodeError.NOT_TOKEN)
              : setError(CodeError.NOT_CODE);
          }
          else {
            const { host, key } = response.data;

            if (host && key) {
              const url = `${host}?redirectKey=${key}`;
              return window.location.replace(url);
            }

            setError(CodeError.SOMETHING_WRONG);
          }
        })
        .finally(() => setRegData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    setFieldValue('code', values.code.replace(NOT_NUMBER, ''));
    if (values.code.length === CODE_LENGTH) {
      handleSubmit();
    }
    if (!!errors.code && touched.code) {
      setError(CodeError.NOT_CODE);
    }
  }, [values.code]);

  const changeErrorState = useCallback(() => {
    setError(null);
  }, []);

  const changeVerifyDataHandler = useCallback(() => {
    setRegData((prevState) => ({ ...prevState, codeError: null }));
    backwardStep();
  }, [backwardStep]);

  const reSendData = {
    url: URL.REGISTRATION_CHECK_PHONE,
    data: { phoneNumber: requisite },
  };

  return (
    <form id={`registrationFormStep_${activeStep}`}
          onSubmit={handleSubmit}
          data-test={`registrationFormStep_${activeStep}`}>
      <FormControl>
        <CodeConfirmation value={values.code}
                          data={regData}
                          changeData={setRegData}
                          reSendData={reSendData}
                          error={error}
                          changeErrorState={changeErrorState}
                          disabled={disabled}
                          changeVerifyData={changeVerifyDataHandler}
                          formikHelpers={{ setFieldValue, setTouched }} />
      </FormControl>
    </form>
  );
};
